import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios';
import { resolveRuntimeApiGatewayUrl } from '@/config/runtime-api';

/**
 * Base API Error class for typed error handling
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public message: string,
    public data?: unknown,
    public validationErrors?: Record<string, string[]>
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Standard API response structure
 */
export interface ApiResponse<T> {
  data: T;
  success: boolean;
  message?: string;
}

/**
 * Paged result structure for list endpoints
 */
export interface PagedResult<T> {
  items: T[];
  totalCount: number;
}

/**
 * Standard query parameters for list endpoints
 */
export interface QueryParams {
  filter?: string;
  sorting?: string;
  skipCount?: number;
  maxResultCount?: number;
}

/**
 * Base Service class providing common API functionality
 * All service classes should extend this to inherit:
 * - Standardized HTTP client configuration
 * - Request/response interceptors
 * - Error handling
 * - Authentication token management
 */
export abstract class BaseService {
  protected api: AxiosInstance;

  constructor(protected basePath: string = '') {
    const gatewayUrl = resolveRuntimeApiGatewayUrl();
    this.api = axios.create({
      baseURL: `${gatewayUrl}${basePath}`,
      timeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '30000'),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  /**
   * Setup request and response interceptors
   */
  private setupInterceptors(): void {
    // Request interceptor - add auth token
    this.api.interceptors.request.use(
      async (config) => {
        const token = await this.getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - handle errors
    this.api.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        return Promise.reject(this.handleError(error));
      }
    );
  }

  /**
   * Get authentication token from OIDC provider
   */
  private async getAuthToken(): Promise<string | null> {
    // Dynamic import to avoid circular dependencies
    const { tokenProvider } = await import('./token-provider');
    return await tokenProvider.getAccessToken();
  }

  /**
   * Handle and transform API errors
   */
  protected handleError(error: AxiosError): ApiError {
    if (error.response) {
      const { status, data } = error.response;
      const errorData = data as {
        message?: string;
        error?: {
          message?: string;
          validationErrors?: Record<string, string[]>;
        };
      };

      const message =
        errorData?.error?.message ||
        errorData?.message ||
        'An unexpected error occurred';

      const validationErrors = errorData?.error?.validationErrors;

      // Handle specific status codes
      switch (status) {
        case 401:
          // Trigger re-authentication
          this.handleUnauthorized();
          break;
        case 403:
          console.warn('Permission denied:', message);
          break;
        case 422:
          console.warn('Validation error:', validationErrors);
          break;
      }

      return new ApiError(status, message, errorData, validationErrors);
    }

    if (error.request) {
      return new ApiError(0, 'Network error - no response received', error.request);
    }

    return new ApiError(0, error.message || 'Request configuration error');
  }

  /**
   * Handle unauthorized errors (401)
   */
  private handleUnauthorized(): void {
    // In local development, suppress hard redirects to avoid auth callback/login loops
    // while backend auth/config is being stabilized.
    if (import.meta.env.DEV) {
      console.warn(
        '[Auth] Received 401 in development mode. Suppressing forced redirect to avoid auth loop.',
      );
      return;
    }

    // In deployed environments keep the existing behavior.
    localStorage.removeItem('access_token');
    window.location.href = '/auth/signin';
  }

  /**
   * Wrap API requests with standardized error handling
   */
  protected async handleRequest<T>(request: Promise<{ data: T }>): Promise<T> {
    try {
      const response = await request;
      return response.data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw this.handleError(error as AxiosError);
    }
  }

  /**
   * Serialize query parameters for API requests
   */
  protected serializeParams(params: QueryParams): Record<string, string | number> {
    const serialized: Record<string, string | number> = {};

    if (params.filter) serialized.Filter = params.filter;
    if (params.sorting) serialized.Sorting = params.sorting;
    if (params.skipCount !== undefined) serialized.SkipCount = params.skipCount;
    if (params.maxResultCount !== undefined) serialized.MaxResultCount = params.maxResultCount;

    return serialized;
  }

  /**
   * Validate and serialize request data
   * Override in child classes for custom validation
   */
  protected validateAndSerialize<T>(data: T): T {
    // Basic validation - override for specific requirements
    if (!data) {
      throw new Error('Request data cannot be null or undefined');
    }
    return data;
  }

  /**
   * GET request helper
   */
  protected async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.handleRequest(this.api.get<T>(url, config));
  }

  /**
   * POST request helper
   */
  protected async post<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
    return this.handleRequest(this.api.post<T>(url, data, config));
  }

  /**
   * PUT request helper
   */
  protected async put<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
    return this.handleRequest(this.api.put<T>(url, data, config));
  }

  /**
   * DELETE request helper
   */
  protected async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.handleRequest(this.api.delete<T>(url, config));
  }

  /**
   * PATCH request helper
   */
  protected async patch<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
    return this.handleRequest(this.api.patch<T>(url, data, config));
  }
}
